import type React from "react";
import { Box, Typography } from "@mui/material";
import type { Proposal } from "../types/proposal.types";
import type { CoverPageTemplate } from "../types/cover.types";

interface CoverPageProps {
  proposal: Proposal;
  template?: CoverPageTemplate | null;
}

const CoverPage: React.FC<CoverPageProps> = ({ proposal, template }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const backgroundImage =
    template?.background_image || "/placeholder.svg?height=1056&width=816";

  const renderLayout1 = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        maxWidth: "800px",
        gap: 4,
      }}
    >
      {/* Service Proposal Title */}
      <Box sx={{ mb: 4 }}>
        {proposal.eventTitle ? (
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: "4.5rem",
              color: template?.theme_color || "#6B4423",
              letterSpacing: "0.05em",
              lineHeight: 1.1,
            }}
          >
            EVENT
          </Typography>
        ) : (
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: "4.5rem",
              color: template?.theme_color || "#6B4423",
              letterSpacing: "0.05em",
              lineHeight: 1.1,
            }}
          >
            SERVICE
          </Typography>
        )}

        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: "4.5rem",
            color: template?.theme_color || "#6B4423",
            letterSpacing: "0.05em",
            lineHeight: 1.1,
          }}
        >
          PROPOSAL
        </Typography>
      </Box>
      {proposal.eventTitle ? (
        // 🌟 Show Event Title
        <Box sx={{ my: 6 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "2rem",
              color: template?.theme_color || "#6B4423",
              letterSpacing: "0.05em",
              lineHeight: 1.1,
            }}
          >
            {proposal.eventTitle}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ my: 6 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.25rem",
              color: template?.theme_color || "#6B4423",
              fontWeight: 500,
              mb: 2,
            }}
          >
            In Loving Memory Of
          </Typography>
          <Typography
            sx={{
              fontSize: "3.5rem",
              color: template?.theme_color || "#6B4423",
              fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
              fontStyle: "italic",
              fontWeight: 400,
            }}
          >
            {proposal.deceasedName}
          </Typography>
        </Box>
      )}

      {/* Client and Date Information */}
      <Box sx={{ mt: 8 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.1rem",
            color: template?.theme_color || "#6B4423",
            fontWeight: 600,
            mb: 1,
          }}
        >
          Client: {proposal.client.name}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.1rem",
            color: template?.theme_color || "#6B4423",
            fontWeight: 600,
          }}
        >
          Date: {formatDate(proposal.createdAt)}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.1rem",
            color: template?.theme_color || "#6B4423",
            fontWeight: 600,
          }}
        >
          Venue:{" "}
          {`${proposal.booking?.services?.[0]?.venue.name || ""} ${
            proposal.booking?.services?.[0]?.venue.address_one || ""
          }`}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      id="cover-page"
      sx={{
        width: "100%",
        height: "100vh",
        minHeight: "1056px",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        p: 6,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {renderLayout1()}
    </Box>
  );
};

export default CoverPage;
